/* screens/checkout/CheckoutInvoice.jsx — экран счёта на безналичную оплату (банк Точка).
   /checkout/invoice/:invoice_id — детали счёта (тариф/сумма/номер), скачивание PDF,
   отправка на email, инструкция «оплатите строго по счёту» и статус с поллингом
   (Ждёт оплаты → Оплачено). Паттерн поллинга — как в CheckoutSuccess (S11):
   первичная загрузка через useFakeQuery + setInterval-опрос до succeeded/failed/timeout.

   PDF открываем ПРЯМОЙ ссылкой в новой вкладке — сессия по cookie (same-origin),
   заголовки не нужны. Вне AppShell (CheckoutLayout).

   РОУТ ВНЕ RequireAuth (гард — RequireAuthOrPayLink). «Выставить счёт» в
   российском B2B и означает «оплатит бухгалтерия», а у бухгалтера аккаунта нет:
   он попадает сюда по `?pl=<токен ссылки>` и читает состояние счёта и PDF
   токен-скоупом. Ему доступны платёжка и статус — то есть ровно то, зачем он
   пришёл. Отправка счёта и акта на произвольный email гостю НЕ даётся (это
   открытый наружу спам-канал), а закрывающий акт — документ клиента и живёт в
   его кабинете. */

import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { PLAN_LABELS } from '../../lib/adapters/checkout.js';
import { paymentLinkInvoicePdfUrl } from '../../lib/api/salesConsole.js';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState, useEffect, useRef } = React;
  const { useParams, useSearchParams, useNavigate } = RR;
  const { useFakeQuery } = hooks;

  const MAX_TICKS = 40;   // 40 × 5с = ~3.3 мин; оплата по счёту приходит не мгновенно
  const TICK_MS = 5000;

  function CheckoutInvoice() {
    const { invoice_id: invoiceId } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    // ws — бизнес-пространство счёта (не активного) → X-Workspace-Id для статуса.
    const ws = params.get('ws') || undefined;
    // Токен именной ссылки: пропуск для того, у кого нет аккаунта.
    const plToken = params.get('pl') || '';
    const guest = !!plToken && !user;

    const fetchInvoice = React.useCallback(
      () => (guest
        ? subscriptionReal.getLinkInvoice(plToken, invoiceId)
        : subscriptionReal.getInvoice(invoiceId, ws)),
      [guest, plToken, invoiceId, ws],
    );

    const { data: invoice, loading, error } =
      useFakeQuery('invoice-' + invoiceId + (ws ? ':' + ws : '') + (guest ? ':g' : ''),
        fetchInvoice, [fetchInvoice]);

    // фаза: 'polling' | 'succeeded' | 'failed' | 'timeout' | 'error'
    const [phase, setPhase] = useState('polling');
    const [paidAt, setPaidAt] = useState(null);
    // Свежий снимок оплаченного счёта: акция в очереди или оплата на разбор.
    const [paidInfo, setPaidInfo] = useState(null);
    const [sending, setSending] = useState(false);
    // Адрес для счёта редактируемый (счёт часто нужен в бухгалтерию, а не на почту
    // аккаунта); префилл — email аккаунта.
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailValue, setEmailValue] = useState(() => (user && user.email) || '');
    // Этап 2: отправка закрывающего акта (появляется после оплаты) — тот же паттерн.
    const [actEmailOpen, setActEmailOpen] = useState(false);
    const [actSending, setActSending] = useState(false);
    const ticks = useRef(0);

    // pdf — прямая ссылка (сессия по cookie); ?ws для бизнес-скоупа не нужен (owner-скоуп по сессии).
    // У гостя сессии нет — берём токен-скоупный прокси той же Точки.
    const pdfHref = guest
      ? paymentLinkInvoicePdfUrl(plToken, invoiceId)
      : '/api/subscription/invoice/' + encodeURIComponent(invoiceId) + '/pdf/';

    useEffect(() => {
      if (!invoiceId || loading) return;
      if (error) { setPhase('error'); return; }
      if (invoice && (invoice.status === 'succeeded' || invoice.paid)) {
        setPhase('succeeded'); setPaidAt(invoice.paid_at); setPaidInfo(invoice);
      } else if (invoice && (invoice.status === 'failed' || invoice.status === 'cancelled')) {
        setPhase('failed');
      }
    }, [invoiceId, loading, error, invoice]);

    useEffect(() => {
      if (!invoiceId || error || phase !== 'polling') return;
      ticks.current = 0;
      const tick = () => {
        ticks.current += 1;
        fetchInvoice().then((st) => {
          if (st.status === 'succeeded' || st.paid) { setPaidAt(st.paid_at); setPaidInfo(st); setPhase('succeeded'); }
          else if (st.status === 'failed' || st.status === 'cancelled') { setPhase('failed'); }
          else if (ticks.current >= MAX_TICKS) { setPhase('timeout'); }
        }).catch(() => { if (ticks.current >= MAX_TICKS) setPhase('error'); });
      };
      const t = setInterval(tick, TICK_MS);
      const first = setTimeout(tick, 1500);
      return () => { clearInterval(t); clearTimeout(first); };
    }, [invoiceId, ws, error, phase, fetchInvoice]);

    const planLabel = (inv) => PLAN_LABELS[inv.plan_slug] || inv.plan_slug;

    const sendEmail = async () => {
      const to = emailValue.trim();
      if (!to) return;
      setSending(true);
      try {
        await subscriptionReal.sendInvoiceEmail(invoiceId, to, ws);
        toast.show('Счёт отправлен на ' + to, 'success');
        setEmailOpen(false);
      } catch (e) {
        toast.show((e && (e.detail || e.message)) || 'Не удалось отправить счёт', 'danger');
      } finally { setSending(false); }
    };

    const sendActEmail = async () => {
      const to = emailValue.trim();
      if (!to) return;
      setActSending(true);
      try {
        await subscriptionReal.sendActEmail(invoiceId, to, ws);
        toast.show('Акт отправлен на ' + to, 'success');
        setActEmailOpen(false);
      } catch (e) {
        toast.show((e && (e.detail || e.message)) || 'Не удалось отправить акт', 'danger');
      } finally { setActSending(false); }
    };

    const headIcon = phase === 'succeeded'
      ? <div className="checkout-icon checkout-icon--ok"><Icon name="check-circle" size={30} /></div>
      : phase === 'failed'
        ? <div className="checkout-icon checkout-icon--bad"><Icon name="x-circle" size={28} /></div>
        : phase === 'timeout' || phase === 'error'
          ? <div className="checkout-icon checkout-icon--warn"><Icon name="clock" size={28} /></div>
          : <div className="checkout-icon"><Icon name="file-text" size={28} /></div>;

    const queuedAt = paidInfo && paidInfo.queued_starts_at;
    const duplicate = !!(paidInfo && paidInfo.needs_review === 'duplicate_promo');
    const underpaid = !!(paidInfo && paidInfo.needs_review === 'underpaid_promo');
    const headTitle = phase === 'succeeded'
      ? (duplicate || underpaid ? 'Оплата получена' : queuedAt ? 'Тариф оплачен' : 'Тариф активирован')
      : phase === 'failed' ? 'Счёт не оплачен'
      : phase === 'timeout' ? 'Ожидаем оплату'
      : phase === 'error' ? 'Не удалось проверить статус'
      : 'Счёт на оплату';

    return (
      <CheckoutLayout>
        <header className="checkout-head checkout-head--center">
          {headIcon}
          <div className="t-eyebrow text-dim">Безналичная оплата подписки</div>
          <h1 className="t-h2">{headTitle}</h1>
        </header>

        {loading && (
          <div className="checkout-state checkout-state--inline"><Icon name="spinner" size={22} /><span className="text-dim">Загружаем счёт…</span></div>
        )}
        {error && (
          <div className="callout callout--bad checkout-banner"><Icon name="x-circle" size={18} /><span>Не удалось загрузить счёт. Обновите страницу или вернитесь к тарифам.</span></div>
        )}

        {invoice && (
          <>
            <div className="checkout-details">
              <CheckoutRow label="Тариф" value={planLabel(invoice)} />
              <CheckoutRow label="Сумма" value={fmt.money(invoice.amount_rub)} mono tone="muted" />
              <CheckoutRow label="Счёт №" value={invoice.number} mono />
              {phase === 'succeeded' && (
                <CheckoutRow label="Оплачено" value={fmt.date(paidAt || invoice.paid_at || new Date())} tone="success" />
              )}
            </div>

            {/* Действия со счётом — доступны, пока счёт не закрыт (актуально в статусе ожидания) */}
            {phase !== 'succeeded' && phase !== 'failed' && (
              <div className="checkout-actions">
                <Button variant="primary" className="full" iconLeft="download"
                  onClick={() => window.open(pdfHref, '_blank', 'noopener')}>
                  Скачать счёт (PDF)
                </Button>
                {/* Отправку на произвольный адрес гостю не даём: анонимный
                    эндпоинт, шлющий письма куда угодно, — готовый спам-канал.
                    PDF он скачивает кнопкой выше, за ней он и пришёл. */}
                {guest ? null : !emailOpen ? (
                  <Button variant="secondary" className="full" iconLeft="mail"
                    onClick={() => setEmailOpen(true)}>
                    Отправить на email
                  </Button>
                ) : (
                  <div className="invoice-email-send">
                    <TextInput type="email" inputMode="email" value={emailValue} autoComplete="email"
                      placeholder="you@company.ru" aria-label="Email для счёта"
                      onChange={(e) => setEmailValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') sendEmail(); }} />
                    <Button variant="primary" iconLeft="mail" loading={sending}
                      disabled={sending || !emailValue.trim()} onClick={sendEmail}>Отправить</Button>
                  </div>
                )}
              </div>
            )}

            {/* Этап 2: закрывающий акт — после оплаты (выставляется автоматически) */}
            {phase === 'succeeded' && invoice.act && (
              <div className="checkout-actions">
                <div className="checkout-progress__row text-dim">
                  <Icon name="file-text" size={16} />
                  <span>Закрывающий акт №{invoice.act.number} от {fmt.date(invoice.act.date)}</span>
                </div>
                <Button variant="secondary" className="full" iconLeft="download"
                  onClick={() => window.open(invoice.act.pdf_url, '_blank', 'noopener')}>
                  Скачать акт (PDF)
                </Button>
                {!actEmailOpen ? (
                  <Button variant="secondary" className="full" iconLeft="mail"
                    onClick={() => setActEmailOpen(true)}>
                    Отправить акт на email
                  </Button>
                ) : (
                  <div className="invoice-email-send">
                    <TextInput type="email" inputMode="email" value={emailValue} autoComplete="email"
                      placeholder="you@company.ru" aria-label="Email для акта"
                      onChange={(e) => setEmailValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') sendActEmail(); }} />
                    <Button variant="primary" iconLeft="mail" loading={actSending}
                      disabled={actSending || !emailValue.trim()} onClick={sendActEmail}>Отправить</Button>
                  </div>
                )}
              </div>
            )}

            {/* Инструкция — критично для автоматического зачёта оплаты */}
            {phase !== 'succeeded' && (
              <div className="callout callout--warn checkout-banner">
                <Icon name="alert-triangle" size={18} />
                <span>
                  Оплатите строго по этому счёту, не меняя назначение платежа — иначе
                  оплата не засчитается автоматически.
                </span>
              </div>
            )}
          </>
        )}

        {/* Статус-прогресс */}
        <div className="checkout-progress" aria-live="polite">
          {phase === 'polling' && (
            <div className="checkout-progress__row text-dim"><Icon name="spinner" size={16} /><span>Ждём оплаты по счёту…</span></div>
          )}
          {phase === 'succeeded' && (
            <div className="checkout-progress__row text-ok"><Icon name="check" size={16} /><span>{duplicate
              ? 'Оплата получена, но этот тариф у вас уже оплачен — мы свяжемся с вами и вернём лишний платёж.'
              : underpaid
                ? 'Сумма меньше цены тарифа при вашей текущей подписке — мы свяжемся с вами, чтобы подключить его или вернуть деньги.'
              : queuedAt
                ? `Оплата получена. Тариф начнёт действовать ${fmt.date(queuedAt)}, когда закончится текущий.`
                : 'Оплата получена, подписка активна.'}</span></div>
          )}
          {phase === 'failed' && (
            <div className="callout callout--bad checkout-banner">
              <Icon name="x-circle" size={18} />
              <span>Оплата по счёту не прошла. Вернитесь к тарифам и выставьте счёт заново{invoice && invoice.failure_reason ? ` (${invoice.failure_reason})` : ''}.</span>
            </div>
          )}
          {phase === 'timeout' && (
            <div className="callout callout--warn checkout-banner">
              <Icon name="clock" size={18} />
              <span>Пока не видим оплату. Обычно зачисление занимает 1–3 рабочих дня — статус обновится автоматически, когда деньги поступят.</span>
            </div>
          )}
          {phase === 'error' && (
            <div className="callout callout--bad checkout-banner">
              <Icon name="alert-triangle" size={18} />
              <span>Не удалось проверить статус. Обновите страницу или вернитесь к тарифам.</span>
            </div>
          )}
        </div>

        {/* Гостю «Настройки» и «К тарифам» ведут в RequireAuth, то есть на
            /signin: после оплаты это читается как «счёт потерялся». Его
            единственный осмысленный путь — назад к счёту по ссылке. */}
        <div className="checkout-actions">
          {phase === 'succeeded' && !guest && (
            <Button variant="primary" className="full" onClick={() => navigate('/settings?tab=business&biz=tariff')}>Перейти в Настройки</Button>
          )}
          {(phase === 'timeout' || phase === 'error') && (
            <Button variant="secondary" className="full" iconLeft="refresh"
              onClick={() => { if (error) window.location.reload(); else setPhase('polling'); }}>Проверить ещё раз</Button>
          )}
          {guest ? (
            <a className="link checkout-link" href={'/pay/' + encodeURIComponent(plToken)}
              onClick={(e) => { e.preventDefault(); navigate('/pay/' + encodeURIComponent(plToken)); }}>Вернуться к счёту</a>
          ) : (
            <a className="link checkout-link" href="/settings?tab=business&biz=tariff"
              onClick={(e) => { e.preventDefault(); navigate('/settings?tab=business&biz=tariff'); }}>К тарифам</a>
          )}
        </div>
      </CheckoutLayout>
    );
  }

export { CheckoutInvoice };
